import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Input, Button, Spin, Layout, Menu, Drawer, Card } from "antd";
import { MessageOutlined, MenuOutlined, DeleteOutlined, PlayCircleOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import "./ChatWithAI.css";

const { TextArea } = Input;

const ChatWithAI = ({ visible, onClose }) => {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const navigate = useNavigate();

  // Load chat history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem("chatHistory");
    if (savedHistory) setChatHistory(JSON.parse(savedHistory));
  }, []);

  // Save chat history to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("chatHistory", JSON.stringify(chatHistory));
  }, [chatHistory]);

  const startNewChat = () => {
    if (conversation.length > 0) saveCurrentChat();
    setConversation([]);
    setCurrentChatId(null);
    setPrompt("");
  };

  const saveCurrentChat = () => {
    if (conversation.length === 0) return;
    const chatTitle = conversation[0].content.slice(0, 30) + (conversation[0].content.length > 30 ? "..." : "");
    const now = new Date().toLocaleString();
    const chatData = { 
      id: currentChatId || Date.now().toString(), 
      title: chatTitle, 
      conversation: [...conversation], 
      timestamp: now 
    };
    setChatHistory(prev => 
      currentChatId ? prev.map(c => c.id === currentChatId ? chatData : c) : [chatData, ...prev]
    );
    setCurrentChatId(chatData.id);
  };

  const loadChat = (chatId) => {
    saveCurrentChat();
    const chat = chatHistory.find(c => c.id === chatId);
    if (chat) { 
      setConversation(chat.conversation); 
      setCurrentChatId(chatId); 
    }
    setSidebarVisible(false);
  };

  const deleteChat = (chatId, e) => {
    e.stopPropagation();
    setChatHistory(prev => prev.filter(chat => chat.id !== chatId));
    if (currentChatId === chatId) { 
      setConversation([]); 
      setCurrentChatId(null); 
    }
  };

  const clearAllChats = () => { 
    setChatHistory([]); 
    setConversation([]); 
    setCurrentChatId(null); 
    localStorage.removeItem("chatHistory"); 
  };

  // Function to handle video navigation
  const handleVideoNavigation = (videoUrl) => {
    handleClose();
    navigate(videoUrl);
  };

  const sendMessage = async () => {
    if (!prompt.trim()) return;
    
    const userMessage = { 
      sender: "user", 
      content: prompt,
      suggestedVideos: [] 
    };
    const updatedConversation = [...conversation, userMessage];
    setConversation(updatedConversation);
    setLoading(true);
    
    try {
      const response = await axios.post("http://localhost:5000/api/chat", { prompt });
      
      const fullReply = response.data.reply;
      const suggestedVideos = response.data.suggestedVideos || [];
      console.log("API Response:", response.data); // Debug log

      // Prepare empty AI message first
      const aiMessage = { sender: "ai", content: "", suggestedVideos };
      const tempConversation = [...updatedConversation, aiMessage];
      setConversation(tempConversation);
      setIsTyping(true);

      let i = 0;
      const typingSpeed = 25;
      const typingInterval = setInterval(() => {
      if (i < fullReply.length) {
        tempConversation[tempConversation.length - 1].content = fullReply.slice(0, i + 1);
        setConversation([...tempConversation]);
        i++;
      } else {
        clearInterval(typingInterval);
        setIsTyping(false);
      }
     }, typingSpeed);
        
      if (!currentChatId) {
        const newChatId = Date.now().toString();
        const chatTitle = prompt.slice(0, 30) + (prompt.length > 30 ? "..." : "");
        const now = new Date().toLocaleString();
        setChatHistory(prev => [{
          id: newChatId, 
          title: chatTitle, 
          conversation: [...tempConversation], 
          timestamp: now 
        }, ...prev]);
        setCurrentChatId(newChatId);
      }
    } catch (error) {
      console.error("Error:", error);
      const errorMessage = {
        sender: "ai",
        content: "⚠️ Server is currently busy. Please try again later.",
        suggestedVideos: []
      };
      setConversation(prev => [...prev, errorMessage]);
    }
    setLoading(false);
    setPrompt("");
  };

  const handleKeyPress = (e) => { 
    if (e.key === "Enter" && !e.shiftKey) { 
      e.preventDefault(); 
      sendMessage(); 
    } 
  };

  const handleClose = () => { 
    saveCurrentChat(); 
    onClose(); 
  };

  // Component to render suggested videos - using cleaner approach
  const SuggestedVideos = ({ videos }) => {
    if (!videos || videos.length === 0) return null;

    return (
      <div className="suggested-videos">
        <div className="suggested-videos-title">
          <PlayCircleOutlined /> Suggested Video Lessons:
        </div>
        <div className="video-list">
          {videos.map((video, index) => (
            <div key={video.id || index} className="video-item">
              <Card
                size="small"
                className="video-suggestion-card"
                actions={[
                  <Button 
                    type="primary" 
                    icon={<PlayCircleOutlined />}
                    onClick={() => handleVideoNavigation(video.url)}
                    size="small"
                  >
                    Watch Now
                  </Button>
                ]}
              >
                <Card.Meta
                  title={video.title}
                  description={
                    <div className="video-card-description">
                      {video.description && (
                        <div className="video-desc">{video.description}</div>
                      )}
                    </div>
                  }
                />
              </Card>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        title={
          <div className="chat-header">
            <div className="chat-header-left">
              <Button 
                type="text" 
                icon={<MenuOutlined />} 
                onClick={() => setSidebarVisible(true)}
                className="chat-header-button"
              />
              <MessageOutlined />
              <span>Beyond AI Assistant</span>
            </div>
            <Button type="text" onClick={startNewChat}>New Chat</Button>
          </div>
        }
        open={visible}
        onCancel={handleClose}
        footer={null}
        width={800}
        className="chat-modal"
      >
        <div className="chat-container">
          <div className="chat-main">
            <div className="chat-messages">
              {conversation.length === 0 ? (
                <div className="chat-empty-state">
                  <MessageOutlined className="chat-empty-icon" />
                  <div>Start a new conversation with Beyond AI</div>
                </div>
              ) : (
                conversation.map((msg, index) => (
                  <div key={index} className={`message-container ${msg.sender === 'user' ? 'user' : ''}`}>
                    <div className={`message-bubble ${msg.sender}`}>
                      {msg.sender === "ai" ? (
                        <>
                          <ReactMarkdown className="react-markdown">{msg.content}</ReactMarkdown>
                          <SuggestedVideos videos={msg.suggestedVideos} />
                        </>
                      ) : (
                        msg.content
                      )}
                    </div>
                  </div>
                ))
              )}
              
              {(loading || isTyping) && (
                <div className="loading-indicator">
                  {isTyping ? "✍️ Typing..." : <><Spin size="small" /> Thinking...</>}
                </div>
              )}
            </div>

            <div className="chat-input-container">
              <TextArea
                rows={2}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Type your question here..."
                onKeyPress={handleKeyPress}
                disabled={loading}
                className="chat-textarea"
              />
              <Button
                type="primary"
                onClick={sendMessage}
                disabled={loading || !prompt.trim()}
                loading={loading}
                className="chat-send-button"
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Drawer
        title="Chat History"
        placement="left"
        onClose={() => setSidebarVisible(false)}
        open={sidebarVisible}
        width={300}
      >
        <div className="sidebar-controls">
          <Button type="primary" onClick={startNewChat}>+ New Chat</Button>
          {chatHistory.length > 0 && (
            <Button danger onClick={clearAllChats}>Clear All</Button>
          )}
        </div>

        <Menu mode="vertical" className="sidebar-menu">
          {chatHistory.map((chat) => (
            <Menu.Item 
              key={chat.id}
              onClick={() => loadChat(chat.id)}
              className={`sidebar-menu-item ${currentChatId === chat.id ? 'active' : ''}`}
            >
              <div className="chat-item">
                <div className="chat-item-content">
                  <div className="chat-item-title">{chat.title}</div>
                  <div className="chat-item-timestamp">{chat.timestamp}</div>
                </div>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  size="small"
                  onClick={(e) => deleteChat(chat.id, e)}
                  className="chat-delete-button"
                />
              </div>
            </Menu.Item>
          ))}
        </Menu>

        {chatHistory.length === 0 && (
          <div className="empty-history">No chat history yet</div>
        )}
      </Drawer>
    </>
  );
};

export default ChatWithAI;